
export class FormToggle extends HTMLElement {
    static get observedAttributes() {
        return ['label', 'name', 'checked', 'disabled'];
    }

    private innerInput: HTMLInputElement | null = null;

    constructor() {
        super();
        this.attachShadow({ mode: 'open' });
    }

    connectedCallback() {
        this.render();
        this.innerInput = this.shadowRoot?.querySelector('input') || null;
        this.setupEventListeners();
    }

    attributeChangedCallback(name: string, oldValue: string, newValue: string) {
        if (oldValue !== newValue) {
            if (name === 'checked' && this.innerInput) {
                this.innerInput.checked = this.hasAttribute('checked');
            } else {
                this.render();
            }
        }
    }

    private setupEventListeners() {
        if (!this.innerInput) return;

        this.innerInput.addEventListener('change', (e) => {
            const checked = (e.target as HTMLInputElement).checked;
            if (checked) {
                this.setAttribute('checked', '');
            } else {
                this.removeAttribute('checked');
            }

            this.dispatchEvent(new CustomEvent('change', {
                bubbles: true,
                composed: true,
                detail: { checked: checked, name: this.getAttribute('name') }
            }));
        });
    }

    get checked() {
        return this.hasAttribute('checked');
    }

    set checked(val: boolean) {
        if (val) {
            this.setAttribute('checked', '');
        } else {
            this.removeAttribute('checked');
        }
    }

    render() {
        const label = this.getAttribute('label') || '';
        const name = this.getAttribute('name') || '';
        const isChecked = this.hasAttribute('checked');
        const disabled = this.hasAttribute('disabled');

        if (this.shadowRoot) {
            this.shadowRoot.innerHTML = `
                <style>
                    :host {
                        display: block;
                        font-family: system-ui, -apple-system, sans-serif;
                    }
                    .toggle-wrapper {
                        display: flex;
                        align-items: center;
                        gap: 0.75rem;
                    }
                    .toggle-switch {
                        position: relative;
                        display: inline-block;
                        width: 44px;
                        height: 24px;
                        flex-shrink: 0;
                    }
                    .toggle-switch input {
                        opacity: 0;
                        width: 0;
                        height: 0;
                    }
                    .slider {
                        position: absolute;
                        cursor: pointer;
                        top: 0;
                        left: 0;
                        right: 0;
                        bottom: 0;
                        background-color: rgba(255, 255, 255, 0.1);
                        transition: .4s;
                        border-radius: 24px;
                        border: 1px solid rgba(255, 255, 255, 0.2);
                    }
                    .slider:before {
                        position: absolute;
                        content: "";
                        height: 18px;
                        width: 18px;
                        left: 2px;
                        bottom: 2px;
                        background-color: #cbd5e1;
                        transition: .4s;
                        border-radius: 50%;
                    }
                    input:checked + .slider {
                        background-color: var(--accent, #f59e0b);
                        border-color: var(--accent, #f59e0b);
                    }
                    input:disabled + .slider {
                        opacity: 0.5;
                        cursor: not-allowed;
                    }
                    input:checked + .slider:before {
                        transform: translateX(20px);
                        background-color: black;
                    }
                    .toggle-label {
                        color: var(--accent, #f59e0b);
                        font-size: 0.7rem;
                        font-weight: 700;
                        text-transform: uppercase;
                        letter-spacing: 0.15em;
                        opacity: 0.8;
                    }
                </style>
                <div class="toggle-wrapper">
                    <label class="toggle-switch">
                        <input type="checkbox" id="${name}" name="${name}" ${isChecked ? 'checked' : ''} ${disabled ? 'disabled' : ''} />
                        <span class="slider"></span>
                    </label>
                    ${label ? `<label for="${name}" class="toggle-label">${label}</label>` : ''}
                </div>
            `;

            this.innerInput = this.shadowRoot.querySelector('input');
            this.setupEventListeners();
        }
    }
}

customElements.define('form-toggle', FormToggle);
